import React from "react";
import {
  TableRow,
  TableCell,
  IconButton,
  Chip,
  Tooltip,
  Typography,
  Box,
} from "@mui/material";
import {
  KeyboardArrowDown,
  KeyboardArrowRight,
  Description,
} from "@mui/icons-material";
import { EntrySummaryRow as EntrySummaryRowType } from "../hooks/useEntrySummaries";
import { formatDateOrTime, numberWithCommas } from "@/utils";
import { HTSCodeWithTooltip } from "@/components/hts-code-with-tooltip";
import {
  getAuditStatusIcon,
  getAuditStatusLabel,
  getAuditStatusStyle,
} from "@/utils/customs-helpers";
import { tokens } from "@/tokens";

// Line column config for expanded view
interface LineColumnConfig {
  key: string;
  label: string;
  align: "left" | "center" | "right";
  format?: (value: any) => string;
}

// All available line columns matching AceDataView
const lineColumns: LineColumnConfig[] = [
  { key: "entrySummaryLineNumber", label: "Line #", align: "center" },
  { key: "htsNumberFull", label: "HTS Code", align: "left" },
  { key: "manufacturerID", label: "Manufacturer ID", align: "left" },
  { key: "countryOfOriginCode", label: "Origin", align: "center" },
  { key: "countryOfExportCode", label: "Export", align: "center" },
  { key: "commercialDescription", label: "Commercial Description", align: "left" },
  {
    key: "lineTariffGoodsValueAmount",
    label: "Goods Value",
    align: "right",
    format: (v) => (v != null ? `$${numberWithCommas(v)}` : "-"),
  },
  {
    key: "totalLineDuties",
    label: "Line Duty Amount",
    align: "right",
    format: (v) => (v != null ? `$${numberWithCommas(v)}` : "-"),
  },
  { key: "filerCode", label: "Filer Code", align: "center" },
  { key: "portOfEntryCode", label: "Port of Entry", align: "center" },
  { key: "entrySummaryDate", label: "Entry Summary Date", align: "center" },
  { key: "entrySummaryCreateDate", label: "Entry Summary Create Date", align: "center" },
  { key: "textileCategoryCode", label: "Textile Category", align: "center" },
  { key: "relatedPartyIndicator", label: "Related Party", align: "center" },
  { key: "ch99CodeList", label: "CH99 Code List", align: "left" },
  {
    key: "mfnDuty",
    label: "MFN Duties",
    align: "right",
    format: (v) => (v != null ? `$${numberWithCommas(v)}` : "-"),
  },
  {
    key: "duty201",
    label: "Section 201 Duties",
    align: "right",
    format: (v) => (v != null ? `$${numberWithCommas(v)}` : "-"),
  },
  {
    key: "duty232",
    label: "Section 232 Duties",
    align: "right",
    format: (v) => (v != null ? `$${numberWithCommas(v)}` : "-"),
  },
  {
    key: "duty301",
    label: "Section 301 Duties",
    align: "right",
    format: (v) => (v != null ? `$${numberWithCommas(v)}` : "-"),
  },
  {
    key: "oth99Duties",
    label: "Other 99 Duties",
    align: "right",
    format: (v) => (v != null ? `$${numberWithCommas(v)}` : "-"),
  },
];

interface SummaryColumn {
  key: string;
  label: string;
  width?: string;
  align: "left" | "center" | "right";
}

interface EntrySummaryRowProps {
  summary: EntrySummaryRowType;
  isExpanded: boolean;
  onToggle: () => void;
  onDocumentsClick: (summary: EntrySummaryRowType) => void;
  onStatusClick?: (summary: EntrySummaryRowType) => void;
  columnCount: number;
  visibleLineColumns?: string[];
  summaryColumns?: SummaryColumn[];
}

const EntrySummaryRowComponent: React.FC<EntrySummaryRowProps> = ({
  summary,
  isExpanded,
  onToggle,
  onDocumentsClick,
  onStatusClick,
  columnCount,
  visibleLineColumns,
  summaryColumns,
}) => {
  const formattedDate = summary.entryDate
    ? formatDateOrTime(summary.entryDate, "date")
    : "-";

  const handleRowClick = (e: React.MouseEvent) => {
    // Don't toggle if clicking on the documents chip
    if ((e.target as HTMLElement).closest('[data-click="documents"]')) {
      return;
    }
    onToggle();
  };

  // Filter line columns based on visibility
  const displayLineColumns = visibleLineColumns
    ? lineColumns.filter((col) => visibleLineColumns.includes(col.key))
    : lineColumns;

  const formatCellValue = (line: TradeDataTypes, column: LineColumnConfig): React.ReactNode => {
    const value = (line as any)[column.key];

    // Special handling for HTS code - wrap with tooltip
    if (column.key === "htsNumberFull" && value) {
      return (
        <HTSCodeWithTooltip htsCode={String(value)}>
          <Typography
            component="span"
            sx={{
              fontSize: "inherit",
              color: tokens.brand.primary,
              cursor: "pointer",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            {value}
          </Typography>
        </HTSCodeWithTooltip>
      );
    }

    if (column.format) return column.format(value);
    if (typeof value === "number") return numberWithCommas(value);
    if (Array.isArray(value)) return value.join(", ");
    return value ?? "-";
  };

  return (
    <>
      {/* Summary Row */}
      <TableRow
        onClick={handleRowClick}
        sx={{
          cursor: "pointer",
          backgroundColor: isExpanded ? tokens.bg.hover : tokens.bg.surface,
          "&:hover": {
            backgroundColor: tokens.bg.hover,
          },
          "& > td": {
            borderBottom: isExpanded ? "none" : `1px solid ${tokens.border.light}`,
          },
        }}
      >
        {/* Expand Icon */}
        <TableCell sx={{ width: 48, p: 1, pl: 1.5 }}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            sx={{ p: 0.5 }}
          >
            {isExpanded ? (
              <KeyboardArrowDown sx={{ color: tokens.brand.primary, fontSize: 20 }} />
            ) : (
              <KeyboardArrowRight sx={{ color: tokens.text.secondary, fontSize: 20 }} />
            )}
          </IconButton>
        </TableCell>

        {/* Entry Summary Number */}
        <TableCell sx={{ fontWeight: tokens.fontWeight.semibold, fontSize: tokens.fontSize.base, color: tokens.text.primary }}>
          {summary.entrySummaryNumber}
        </TableCell>

        {/* Entry Date */}
        <TableCell align="center" sx={{ fontSize: tokens.fontSize.base, color: tokens.text.secondary }}>
          {formattedDate}
        </TableCell>

        {/* Port */}
        <TableCell align="center" sx={{ fontSize: tokens.fontSize.base, color: tokens.text.secondary }}>
          {summary.portOfEntryCode || "-"}
        </TableCell>

        {/* Line Count */}
        <TableCell align="center" sx={{ fontSize: tokens.fontSize.base, color: tokens.text.primary }}>
          <Chip
            label={summary.lineCount}
            size="small"
            sx={{
              height: 24,
              minWidth: 28,
              fontSize: tokens.fontSize.xs,
              fontWeight: tokens.fontWeight.semibold,
              borderRadius: "9999px",
              ...(summary.lineCount >= 15
                ? { bgcolor: tokens.status.warningBg, color: "#92400E" }
                : summary.lineCount >= 5
                  ? { bgcolor: tokens.status.infoBg, color: "#1D4ED8" }
                  : { bgcolor: "#D1FAE5", color: "#047857" }),
            }}
          />
        </TableCell>

        {/* Total Value */}
        <TableCell align="right" sx={{ fontSize: tokens.fontSize.base, color: tokens.text.primary, fontWeight: tokens.fontWeight.normal }}>
          ${numberWithCommas(summary.totalGoodsValue)}
        </TableCell>

        {/* Total Duties */}
        <TableCell align="right" sx={{ fontSize: tokens.fontSize.base, color: tokens.text.primary, fontWeight: tokens.fontWeight.normal }}>
          ${numberWithCommas(summary.totalLineDuties)}
        </TableCell>

        {/* Documents */}
        <TableCell align="center">
          <Tooltip title={summary.documentCount > 0 ? "View linked documents" : "No documents linked"}>
            <Box
              data-click="documents"
              onClick={(e) => {
                e.stopPropagation();
                onDocumentsClick(summary);
              }}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                cursor: "pointer",
                borderRadius: tokens.radius.sm,
                px: 1,
                py: 0.5,
                "&:hover": {
                  bgcolor: summary.documentCount > 0 ? tokens.bg.muted : "transparent",
                },
              }}
            >
              <Description sx={{ fontSize: 16, color: tokens.text.muted }} />
              <Typography sx={{ fontSize: tokens.fontSize.base, color: tokens.text.secondary, fontWeight: tokens.fontWeight.normal }}>
                {summary.documentCount}
              </Typography>
            </Box>
          </Tooltip>
        </TableCell>

        {/* Audit Status */}
        <TableCell align="center">
          {summary.latestAuditStatus ? (
            <Tooltip title="Click to view audit details">
              <Chip
                icon={getAuditStatusIcon(summary.latestAuditStatus) || undefined}
                label={getAuditStatusLabel(summary.latestAuditStatus!, true)}
                size="small"
                variant="outlined"
                onClick={(e) => {
                  e.stopPropagation();
                  onStatusClick?.(summary);
                }}
                sx={{
                  height: 24,
                  fontSize: tokens.fontSize.xs,
                  fontWeight: tokens.fontWeight.normal,
                  cursor: "pointer",
                  ...getAuditStatusStyle(summary.latestAuditStatus),
                  "& .MuiChip-icon": {
                    marginLeft: "6px",
                    color: "inherit",
                  },
                }}
              />
            </Tooltip>
          ) : (
            <Typography variant="caption" sx={{ color: tokens.text.muted }}>
              -
            </Typography>
          )}
        </TableCell>

        {/* Optional dynamic columns — only rendered when present in summaryColumns */}
        {summaryColumns
          ?.filter((col) =>
            ["liquidationStatus", "liquidationDate", "entrySummaryVersionNumber"].includes(col.key)
          )
          .map((col) => {
            const value = (summary as any)[col.key];
            let display: React.ReactNode = value ?? "-";
            if (col.key === "liquidationDate" && value) {
              display = formatDateOrTime(value, "date");
            } else if (col.key === "entrySummaryVersionNumber" && value != null && value !== 0) {
              display = String(value);
            } else if (!value || value === "") {
              display = "-";
            }
            return (
              <TableCell
                key={col.key}
                align={col.align}
                sx={{ fontSize: tokens.fontSize.base, color: tokens.text.secondary }}
              >
                {display}
              </TableCell>
            );
          })}
      </TableRow>

      {/* Expanded Line Items - only render when expanded */}
      {isExpanded && (
        <TableRow>
          <TableCell
            colSpan={columnCount}
            sx={{
              p: 0,
              borderBottom: `1px solid ${tokens.border.light}`,
            }}
          >
            {/* Scrollable container for line items */}
            <Box
              sx={{
                width: 0,
                minWidth: "100%",
                maxHeight: 320,
                overflowY: "auto",
                overflowX: "auto",
                "&::-webkit-scrollbar": {
                  width: 6,
                  height: 6,
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: tokens.scrollbar.track,
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: tokens.scrollbar.thumb,
                  borderRadius: 3,
                  "&:hover": {
                    backgroundColor: tokens.scrollbar.thumbHover,
                  },
                },
              }}
            >
              {/* Line items header row */}
              <TableRow
                component="div"
                sx={{
                  display: "flex",
                  minWidth: "fit-content",
                  backgroundColor: "#E6ECED",
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                }}
              >
              {displayLineColumns.map((col) => {
                const isSmallCol = ["entrySummaryLineNumber", "countryOfOriginCode", "countryOfExportCode"].includes(col.key);
                return (
                  <TableCell
                    component="div"
                    key={col.key}
                    align={col.align}
                    sx={{
                      flex: col.key === "commercialDescription" ? 2 : isSmallCol ? "none" : 1,
                      width: isSmallCol ? 50 : "auto",
                      minWidth: isSmallCol ? 50 : 100,
                      fontWeight: tokens.fontWeight.semibold,
                      fontSize: tokens.fontSize.xs,
                      lineHeight: "16px",
                      color: tokens.text.primary,
                      textTransform: "none",
                      letterSpacing: "normal",
                      padding: "7px 12px",
                      border: "none",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: col.align === "right" ? "flex-end" : col.align === "center" ? "center" : "flex-start",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {col.label}
                  </TableCell>
                );
              })}
            </TableRow>

            {/* Line item rows */}
            {summary.lines.map((line, index) => (
              <TableRow
                component="div"
                key={`${line.entrySummaryNumber}-${line.entrySummaryLineNumber}-${index}`}
                sx={{
                  display: "flex",
                  minWidth: "fit-content",
                  backgroundColor: index % 2 === 0 ? tokens.bg.surface : "#f5f7f9",
                  "&:hover": {
                    backgroundColor: "#e8ecef",
                  },
                  "& > *": {
                    borderBlock: `1px solid ${tokens.border.default}`,
                  },
                }}
              >
                {displayLineColumns.map((col) => {
                  const isSmallCol = ["entrySummaryLineNumber", "countryOfOriginCode", "countryOfExportCode"].includes(col.key);
                  return (
                    <TableCell
                      component="div"
                      key={col.key}
                      align={col.align}
                      sx={{
                        flex: col.key === "commercialDescription" ? 2 : isSmallCol ? "none" : 1,
                        width: isSmallCol ? 50 : "auto",
                        minWidth: isSmallCol ? 50 : 100,
                        fontSize: tokens.fontSize.xs,
                        fontWeight: tokens.fontWeight.normal,
                        lineHeight: "16px",
                        color: tokens.text.primary,
                        padding: "7px 12px",
                        border: "none",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: col.align === "right" ? "flex-end" : col.align === "center" ? "center" : "flex-start",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: col.key === "commercialDescription" ? "normal" : "nowrap",
                      }}
                    >
                      {formatCellValue(line, col)}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
            </Box>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

export default EntrySummaryRowComponent;
